// Pure utility functions for parsing and formatting MIDI messages.
// No React or store dependencies — safe to use anywhere.

export interface MidiMessage {
  timestamp: number
  type: 'noteon' | 'noteoff' | 'cc' | 'pitchbend' | 'programchange' | 'other'
  channel: number // 1-16
  note?: number // 0-127
  velocity?: number // 0-127
  cc?: number // controller number
  value?: number // controller value
  raw: number[]
  // Source identification — set by `useWebMidi` when piping a real
  // hardware input or by the virtual-keyboard dispatcher. Optional
  // because `parseMidiMessage` is also used as a pure parser in tests
  // and on a fresh `Uint8Array` with no source context.
  inputId?: string
  inputName?: string
}

const NOTE_NAMES = [
  'C',
  'C#',
  'D',
  'D#',
  'E',
  'F',
  'F#',
  'G',
  'G#',
  'A',
  'A#',
  'B',
] as const

/** Convert a MIDI note number (0-127) to a human-readable name, e.g. 60 → "C4". */
export function noteNumberToName(note: number): string {
  const octave = Math.floor(note / 12) - 1
  const name = NOTE_NAMES[note % 12]
  return `${name}${octave}`
}

/** Parse raw MIDI bytes into a structured MidiMessage. */
export function parseMidiMessage(
  data: Uint8Array,
  timestamp?: number,
): MidiMessage {
  const raw = Array.from(data)
  const status = data[0]
  const channel = (status & 0x0f) + 1
  const command = status & 0xf0
  const ts = timestamp ?? Date.now()

  switch (command) {
    case 0x90: // Note On (velocity 0 = Note Off)
      return data[2] > 0
        ? {
            timestamp: ts,
            type: 'noteon',
            channel,
            note: data[1],
            velocity: data[2],
            raw,
          }
        : {
            timestamp: ts,
            type: 'noteoff',
            channel,
            note: data[1],
            velocity: 0,
            raw,
          }
    case 0x80: // Note Off
      return {
        timestamp: ts,
        type: 'noteoff',
        channel,
        note: data[1],
        velocity: data[2],
        raw,
      }
    case 0xb0: // Control Change
      return {
        timestamp: ts,
        type: 'cc',
        channel,
        cc: data[1],
        value: data[2],
        raw,
      }
    case 0xe0: // Pitch Bend
      return {
        timestamp: ts,
        type: 'pitchbend',
        channel,
        value: data[1] | (data[2] << 7),
        raw,
      }
    case 0xc0: // Program Change
      return {
        timestamp: ts,
        type: 'programchange',
        channel,
        value: data[1],
        raw,
      }
    default:
      return { timestamp: ts, type: 'other', channel, raw }
  }
}

/** Human-readable label for a MIDI message type. */
export function midiMessageTypeLabel(type: MidiMessage['type']): string {
  switch (type) {
    case 'noteon':
      return 'Note On'
    case 'noteoff':
      return 'Note Off'
    case 'cc':
      return 'CC'
    case 'pitchbend':
      return 'Pitch Bend'
    case 'programchange':
      return 'Prog Change'
    case 'other':
      return 'Other'
  }
}

/** Badge color for the MIDI monitor. */
export function midiMessageColor(type: MidiMessage['type']): string {
  switch (type) {
    case 'noteon':
      return '#4ECDC4'
    case 'noteoff':
      return '#7B8794'
    case 'cc':
      return '#F59E0B'
    case 'pitchbend':
      return '#A855F7'
    case 'programchange':
      return '#3B82F6'
    case 'other':
      return '#6B7280'
  }
}
